import getConfig from '~/config';
import { formatTemporal } from '@/shared/utils/temporal';
import { useTemporalInput, type TemporalDraftProps } from '../useTemporalInput';
import { MaskedInput } from '~/molecules/components/TextInput/MaskedInput';
import {
    FormControl,
    Text,
    TimePicker,
    TextInput,
    IconButton,
    useShortcut,
} from '~/molecules';
import { ReadonlyField } from '../ReadonlyField';
import { StyleSheet } from 'react-native-unistyles';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { formatTimeValue, parseTimeValue } from './utils';
import { FocusTrap } from 'focus-trap-react';
import { View } from 'react-native';

export interface TimeFieldProps extends TemporalDraftProps {
    disabled?: boolean;
    isReadonly?: boolean;
    name?: string;
    onChange?: (value: string | null) => void;
    onKeyPress?: (e: unknown) => void;
    placeholder?: string;
    required?: boolean;
    title: string;
    tooltip?: string;
    value: string | Date | null;
    validationMessage?: ReactNode;
}

const FOCUS_TRAP_OPTIONS = {
    allowOutsideClick: true,
    initialFocus: false,
} as const;

export const TimeField = (props: TimeFieldProps) => {
    const { isReadonly, name, onChange, title, tooltip, validationMessage, value, temporalDraft, onTemporalDraftChange, ...rest } = props;
    const [open, setOpen] = useState(false);

    useShortcut('modal-close', () => {
        if (open) setOpen(false);
    });

    const dateValue = useMemo(() => parseTimeValue(value), [value]);

    const locale = getConfig().translations?.locale || 'de';
    const input = useTemporalInput('time', typeof value === 'string' ? value : formatTimeValue(dateValue), onChange, { temporalDraft, onTemporalDraftChange }, locale);
    const errorId = useId();

    if (isReadonly) {
        return (
            <ReadonlyField
                name={name}
                label={title}
                value={formatTemporal(typeof value === 'string' ? value : formatTimeValue(parseTimeValue(value)), 'time', locale)}
                tooltip={tooltip}
            />
        );
    }

    const handleTimeChange = (nextValue: Date | null) => {
        if (props.disabled) return;
        const nextDate = nextValue ? new Date(nextValue) : null;
        if (nextDate) nextDate.setSeconds(dateValue?.getSeconds() ?? 0);
        const next = formatTimeValue(nextDate);
        input.commit(next);
    };

    return (
        <FormControl disabled={rest.disabled}>
            <FormControl.Label label={title} tooltip={tooltip} required={props.required} />
            <TimePicker.Provider
                disabled={rest.disabled}
                is24Hour
                mode="time"
                open={open}
                onChange={handleTimeChange}
                onOpenChange={setOpen}
                value={dateValue}
            >
                <MaskedInput
                    {...rest}
                    {...input.inputProps}
                    mask="HH:mm"
                    keyboardType="number-pad"
                    accessibilityLabel={name ?? title}
                    aria-describedby={validationMessage ? errorId : undefined}
                    aria-invalid={!!validationMessage}
                    error={!!(validationMessage)}
                    placeholderTextColor={props.disabled ? styles.disabledText.color : styles.placeholderTextColor.color}
                >
                    <TextInput.Right>
                        <TimePicker.Trigger>
                            <IconButton
                                accessibilityLabel="clock"
                                color={props.disabled ? styles.disabledIcon.color : styles.iconButton.color}
                                name="clock-outline"
                                shape="square"
                                size={18}
                                style={styles.iconButton}
                            />
                        </TimePicker.Trigger>
                    </TextInput.Right>
                </MaskedInput>
                <TimePicker.Modal label={title} uppercase>
                    <FocusTrap active={open} focusTrapOptions={FOCUS_TRAP_OPTIONS}>
                        <View collapsable={false}>
                            <TimePicker.Clock />
                        </View>
                    </FocusTrap>
                </TimePicker.Modal>
            </TimePicker.Provider>
            {(validationMessage) && (
                <Text nativeID={errorId} role="alert" style={styles.validationError}>{validationMessage}</Text>
            )}
        </FormControl>
    );
};

TimeField.displayName = 'TimeField';

const styles = StyleSheet.create((theme) => ({
    disabledText: { color: theme.colors.onSurfaceDisabled },
    disabledIcon: { color: theme.colors.onSurface },
    validationError: { color: theme.colors.error, fontSize: 13 },
    iconButton: {
        color: theme.colors.primary,
        height: 30,
        width: 30,
    },
    placeholderTextColor: {
        color: theme.colors.onSurfaceVariant,
    },
}));
